// De browserkant van pushmeldingen.
//
// Drie partijen moeten het eens zijn voor er een bericht aankomt: de browser
// geeft toestemming, de pushdienst van Google of Apple maakt een adres aan, en
// onze server bewaart dat adres. Dit bestand doet die dans, en geeft in gewone
// taal terug waar het misgaat - want dat is bij pushmeldingen zelden duidelijk.

import { favorietSleutel, type Favoriet } from "@/lib/favorieten";

export type Soort = "wedstrijddag" | "mijnTeams" | "uitslag" | "uitstel";

const SOORTEN_SLEUTEL = "ntto-meldingsoorten";

export const STANDAARD_SOORTEN: Soort[] = ["mijnTeams", "uitslag", "uitstel"];

export type Gesteldheid =
  | { staat: "laden" }
  | { staat: "kan-niet"; reden: string }
  | { staat: "ios-installeren" }
  | { staat: "geweigerd" }
  | { staat: "uit" }
  | { staat: "aan" };

/** Draait deze pagina als geïnstalleerde app op het beginscherm? */
function alsApp(): boolean {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    // Safari op iOS gebruikt zijn eigen vlag.
    (navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

function isIOS(): boolean {
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
}

export async function leesGesteldheid(): Promise<Gesteldheid> {
  if (!("serviceWorker" in navigator)) {
    return { staat: "kan-niet", reden: "Deze browser ondersteunt geen meldingen." };
  }
  if (!("PushManager" in window) || !("Notification" in window)) {
    // iOS ondersteunt push alleen vanaf het beginscherm; dat is geen defect
    // maar een instelling, dus dat zeggen we ook zo.
    if (isIOS() && !alsApp()) return { staat: "ios-installeren" };
    return { staat: "kan-niet", reden: "Deze browser ondersteunt geen meldingen." };
  }
  if (isIOS() && !alsApp()) return { staat: "ios-installeren" };

  if (Notification.permission === "denied") return { staat: "geweigerd" };

  const reg = await navigator.serviceWorker.getRegistration();
  const bestaand = await reg?.pushManager.getSubscription();
  return bestaand ? { staat: "aan" } : { staat: "uit" };
}

// --- gekozen soorten als externe bron ------------------------------------
//
// Net als bij de favorieten: localStorage is voor React een externe bron, dus
// abonneren en een momentopname. De momentopname moet tussen twee aanroepen
// dezelfde verwijzing houden, anders blijft React opnieuw renderen.

let soortenCache: Soort[] | null = null;
const soortenLuisteraars = new Set<() => void>();

function leesSoorten(): Soort[] {
  try {
    const ruw = window.localStorage.getItem(SOORTEN_SLEUTEL);
    if (!ruw) return STANDAARD_SOORTEN;
    const gelezen: unknown = JSON.parse(ruw);
    return Array.isArray(gelezen) ? (gelezen as Soort[]) : STANDAARD_SOORTEN;
  } catch {
    return STANDAARD_SOORTEN;
  }
}

export function abonneerSoorten(bij: () => void): () => void {
  soortenLuisteraars.add(bij);
  return () => soortenLuisteraars.delete(bij);
}

export function soortenMomentopname(): Soort[] {
  soortenCache ??= leesSoorten();
  return soortenCache;
}

/** Op de server bestaat er geen opslag; altijd dezelfde standaardlijst. */
export function soortenServerMomentopname(): Soort[] {
  return STANDAARD_SOORTEN;
}

export function bewaarSoorten(soorten: Soort[]): void {
  try {
    window.localStorage.setItem(SOORTEN_SLEUTEL, JSON.stringify(soorten));
  } catch {
    // Een privévenster mag de instelling niet laten mislukken.
  }
  soortenCache = null;
  for (const l of soortenLuisteraars) l();
}

// De VAPID-sleutel komt als base64url binnen; de pushdienst wil ruwe bytes.
function naarBytes(base64url: string): Uint8Array<ArrayBuffer> {
  const opgevuld = base64url.padEnd(base64url.length + ((4 - (base64url.length % 4)) % 4), "=");
  const ruw = window.atob(opgevuld.replace(/-/g, "+").replace(/_/g, "/"));
  const bytes = new Uint8Array(new ArrayBuffer(ruw.length));
  for (let i = 0; i < ruw.length; i++) bytes[i] = ruw.charCodeAt(i);
  return bytes;
}

async function wachtOpServiceWorker(): Promise<ServiceWorkerRegistration> {
  const bestaand = await navigator.serviceWorker.getRegistration();
  if (bestaand) return bestaand;
  // In ontwikkelmodus registreert de app hem niet; hier alsnog, want zonder
  // service worker is er geen push. De vlag zegt hem dat hij niets mag cachen,
  // zodat hij de hot reload niet in de weg zit.
  const adres = process.env.NODE_ENV === "production" ? "/sw.js" : "/sw.js?ontwikkel=1";
  await navigator.serviceWorker.register(adres);
  return navigator.serviceWorker.ready;
}

/**
 * Zet meldingen aan. Levert de nieuwe gesteldheid op, plus een uitleg als het
 * niet gelukt is.
 */
export async function zetAan(
  favorieten: Favoriet[],
  soorten: Soort[],
): Promise<{ gesteldheid: Gesteldheid; fout?: string }> {
  const sleutel = process.env.NEXT_PUBLIC_VAPID_PUBLIEKE_SLEUTEL;
  if (!sleutel) {
    return {
      gesteldheid: { staat: "kan-niet", reden: "Meldingen staan niet aan op deze server." },
      fout: "geen sleutel",
    };
  }

  const toestemming = await Notification.requestPermission();
  if (toestemming !== "granted") {
    return { gesteldheid: toestemming === "denied" ? { staat: "geweigerd" } : { staat: "uit" } };
  }

  const reg = await wachtOpServiceWorker();
  const aanmelding =
    (await reg.pushManager.getSubscription()) ??
    (await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: naarBytes(sleutel),
    }));

  const res = await stuurNaarServer(aanmelding, favorieten, soorten, true);
  if (!res.ok) {
    return { gesteldheid: { staat: "uit" }, fout: "De server nam de aanmelding niet aan." };
  }
  return { gesteldheid: { staat: "aan" } };
}

export async function zetUit(): Promise<Gesteldheid> {
  const reg = await navigator.serviceWorker.getRegistration();
  const aanmelding = await reg?.pushManager.getSubscription();
  if (aanmelding) {
    await fetch("/api/meldingen", {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ endpoint: aanmelding.endpoint }),
    }).catch(() => {});
    await aanmelding.unsubscribe().catch(() => {});
  }
  return { staat: "uit" };
}

// Wat we het laatst hebben doorgegeven. Zonder dit stuurt het aanzetten meteen
// twee keer hetzelfde: eenmaal vanuit de knop, en nog eens omdat de instelling
// daarna op "aan" springt en het bijwerk-effect afgaat.
let laatstVerstuurd = "";

function stuurNaarServer(
  aanmelding: PushSubscription,
  favorieten: Favoriet[],
  soorten: Soort[],
  test = false,
): Promise<Response> {
  const json = aanmelding.toJSON();
  laatstVerstuurd = kenmerk(aanmelding.endpoint, favorieten, soorten);
  return fetch("/api/meldingen", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      endpoint: aanmelding.endpoint,
      sleutels: json.keys,
      // Drie lijsten in plaats van één, zodat de teams blijven staan waar de
      // server ze sinds augustus zoekt.
      volgt: favorieten.flatMap((f) => (f.soort === "team" ? [{ sectie: f.sectie, id: f.id }] : [])),
      wedstrijden: favorieten.flatMap((f) => (f.soort === "wedstrijd" ? [f.id] : [])),
      klassen: favorieten.flatMap((f) => (f.soort === "klasse" ? [f.sleutel] : [])),
      soorten,
      test,
    }),
  });
}

/**
 * Stuurt de huidige favorieten en voorkeuren opnieuw op. Doet niets als er
 * geen aanmelding is - dan valt er ook niets bij te werken.
 */
export async function werkBij(favorieten: Favoriet[], soorten: Soort[]): Promise<void> {
  if (!("serviceWorker" in navigator)) return;
  const reg = await navigator.serviceWorker.getRegistration();
  const aanmelding = await reg?.pushManager.getSubscription();
  if (!aanmelding) return;
  if (kenmerk(aanmelding.endpoint, favorieten, soorten) === laatstVerstuurd) return;
  await stuurNaarServer(aanmelding, favorieten, soorten).catch(() => {});
}

function kenmerk(endpoint: string, favorieten: Favoriet[], soorten: Soort[]): string {
  return JSON.stringify([
    endpoint,
    favorieten.map(favorietSleutel).sort(),
    [...soorten].sort(),
  ]);
}
